import { useEffect, useState } from "react";

import { Button, Classes, Colors, Icon } from "@blueprintjs/core";
import { Link, useLocation, useParams } from "react-router";

import { useUnreadMessages } from "@/components/helpers/custom-hooks/use-unread-messages";
import { isCoach } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import {
  CardContainer,
  MessageCountContainer,
} from "@/components/pages_logged_in/workspace/messages/StyledContainers";
import { getReviewColor } from "@/components/pieces/coaching/reviews/utils";
import { SmallCaps } from "@/components/pieces/form/SmallCaps";
import { Truncate } from "@/components/pieces/Truncate";
import { getUser } from "@/components/session/JwtTokenManager";
import { SUBTEXT_COLOR } from "@/css/constants";
import { ChatReviewsQuery, useChatReviewsLazyQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

const NUM_PER_PAGE = 5;

type ChatReview = NonNullableArrayItem<ChatReviewsQuery["chatReviews"]>;

interface ReviewListProps {
  customerId: string;
  onMessageRead: () => void;
}

export function ReviewList({ customerId, onMessageRead }: ReviewListProps) {
  const [reviews, setReviews] = useState<ChatReview[]>([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const location = useLocation();

  const [fetch, { loading }] = useChatReviewsLazyQuery({
    onCompleted: (data) => {
      setLoadedOnce(true);
      setLoadingMore(false);

      const chatReviews = ensureNonNullArray(data?.chatReviews);

      if (!chatReviews.length) {
        setHasMore(false);
        return;
      }

      if (chatReviews.length < NUM_PER_PAGE) {
        setHasMore(false);
      }

      if (isRefetching) {
        setReviews(chatReviews);
        setIsRefetching(false);
      } else {
        // Merge, dedupe, and set
        setReviews((oldReviews) =>
          [...oldReviews, ...chatReviews].filter(
            (wccs, index, self) =>
              index === self.findIndex((t) => t.id === wccs.id),
          ),
        );
      }
    },
  });

  function loadMore(offset = 0, limit = NUM_PER_PAGE) {
    fetch({
      variables: { customerId, offset, limit },
    });
  }

  useEffect(() => {
    setIsRefetching(true);
    loadMore(0, reviews.length);
  }, [location.pathname, location.search]);

  // Fetch once when the component is loaded
  useEffect(() => {
    loadMore();
  }, []);

  if (loading && !loadedOnce) {
    return <Box m={3} className={`${Classes.SKELETON} round-corners`} p={4} />;
  }

  if (!reviews.length) return null;

  return (
    <Box>
      <SmallCaps mt={24} mb={12} mx={3} style={{ fontSize: 13 }}>
        <b>Reviews</b>
      </SmallCaps>
      {reviews.map((review) => {
        return (
          <Box key={review.id}>
            <ReviewCard
              review={review}
              showMessageCount={true}
              onMessageRead={onMessageRead}
              chatChannelId={review.chatChannelId ?? ""}
            />
          </Box>
        );
      })}
      {hasMore ? (
        <Box pt={2} px={3}>
          <Button
            fill={true}
            loading={loadingMore}
            onClick={() => {
              loadMore(reviews.length);
              setLoadingMore(true);
            }}
          >
            Load More
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

interface ReviewCardProps {
  review: ChatReview;
  onMessageRead: () => void;
  showMessageCount: boolean;
  chatChannelId: string;
}

function ReviewCard({
  review,
  onMessageRead,
  showMessageCount,
  chatChannelId,
}: ReviewCardProps) {
  const { chatChannelTitle, status, unreadMessages } = review;
  const { userId, channelId } = useParams();
  const selected = channelId === `Review_${review.id}`;
  const { unreadMessageCount, containerRef } = useUnreadMessages({
    showMessageCount,
    chatChannelId,
  });
  const location = useLocation();

  useEffect(() => {
    if (unreadMessageCount === null || unreadMessageCount === 0) {
      return;
    }
    if (unreadMessageCount !== unreadMessages) {
      setTimeout(onMessageRead, 200);
    }
  }, [unreadMessageCount]);

  const countToDisplay =
    unreadMessageCount === null ? unreadMessages : unreadMessageCount;

  let link = `/messages/${getUser()?.id}/channels/Review_${review.id}`;
  if (isCoach()) {
    link = location.pathname.startsWith("/client")
      ? `/client/${userId}/messages/Review_${review.id}`
      : `/messages/${userId}/channels/Review_${review.id}`;
  }

  return (
    <CardContainer ref={containerRef} className={selected ? "selected" : ""}>
      <Link to={link} style={{ color: Colors.BLACK }}>
        <Box p={3}>
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center" mb={1}>
              <Flex
                mr={2}
                justifyContent="center"
                height={34}
                width={34}
                alignItems="center"
                backgroundColor={getReviewColor(review)}
                style={{ borderRadius: "100%" }}
              >
                <Icon icon="annotation" size={18} color="#FFF" />
              </Flex>
              <Box flex={1}>
                <Truncate className="name">{chatChannelTitle}</Truncate>
                <Box color={SUBTEXT_COLOR} fontSize={13}>
                  {status}
                </Box>
              </Box>
            </Flex>
            {countToDisplay ? (
              <MessageCountContainer
                alignItems="center"
                justifyContent="center"
                style={{ fontSize: countToDisplay > 99 ? 13 : 14 }}
              >
                {countToDisplay > 99 ? "99+" : countToDisplay}
              </MessageCountContainer>
            ) : null}
          </Flex>
        </Box>
      </Link>
    </CardContainer>
  );
}
